import { useEffect, useState, useCallback } from "react";
import { authAxios } from "@/utils/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Database, CheckCircle2, AlertTriangle, PlayCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * MigrationsCard — admin UI to run idempotent database migrations.
 *
 * Each migration calls a POST endpoint under /api/admin/migrations and
 * displays the human-readable result. Safe to re-run; backend scripts
 * are idempotent by design.
 *
 * Used at the bottom of AdminSettings → Settings tab.
 */
export default function MigrationsCard() {
  const [migrations, setMigrations] = useState([]);
  const [results, setResults] = useState({}); // { [id]: { results, ts } }
  const [running, setRunning] = useState({}); // { [id]: boolean }
  const [loadingList, setLoadingList] = useState(true);

  const loadMigrations = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await authAxios.get("/admin/migrations");
      setMigrations(res.data?.migrations || []);
    } catch (err) {
      console.error("Failed to load migrations:", err);
      toast.error("Failed to load migration list");
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadMigrations();
  }, [loadMigrations]);

  const runMigration = async (id, label) => {
    setRunning((r) => ({ ...r, [id]: true }));
    try {
      const res = await authAxios.post(`/admin/migrations/${id}`);
      const payload = res.data || {};
      setResults((prev) => ({
        ...prev,
        [id]: { results: payload.results || [], ts: new Date().toISOString() },
      }));
      toast.success(`${label} — OK`);
    } catch (err) {
      console.error(`Migration ${id} failed:`, err);
      const detail = err?.response?.data?.detail || err?.message || "Failed";
      setResults((prev) => ({
        ...prev,
        [id]: { error: detail, ts: new Date().toISOString() },
      }));
      toast.error(`${label} — ${detail}`);
    } finally {
      setRunning((r) => ({ ...r, [id]: false }));
    }
  };

  return (
    <Card className="bg-divine-charcoal border-divine-gold/30" data-testid="admin-migrations-card">
      <CardHeader>
        <CardTitle className="text-divine-gold flex items-center gap-2">
          <Database className="w-5 h-5" /> Database Migrations
        </CardTitle>
        <CardDescription className="text-white/60">
          Run idempotent one-off database migrations against the live DB.
          Safe to re-run — each migration is a no-op when already applied.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadingList ? (
          <div className="flex items-center justify-center py-6 text-white/50" data-testid="admin-migrations-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : migrations.length === 0 ? (
          <div className="text-sm text-white/50 py-6" data-testid="admin-migrations-empty">
            No migrations registered.
          </div>
        ) : (
          <div className="space-y-4">
            {migrations.map((m) => {
              const r = results[m.id];
              const isRunning = !!running[m.id];
              return (
                <div
                  key={m.id}
                  className="rounded-lg border border-divine-gold/15 bg-divine-black/40 p-4 space-y-3"
                  data-testid={`admin-migration-row-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-divine-gold">{m.label}</div>
                      <div className="text-xs text-white/60 mt-1">{m.description}</div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      disabled={isRunning}
                      onClick={() => runMigration(m.id, m.label)}
                      className="bg-divine-gold/20 hover:bg-divine-gold/40 text-divine-gold border border-divine-gold/40 disabled:opacity-40"
                      data-testid={`admin-migration-run-${m.id}`}
                    >
                      {isRunning ? (
                        <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Running…</>
                      ) : (
                        <><PlayCircle className="w-3.5 h-3.5 mr-1" /> Run</>
                      )}
                    </Button>
                  </div>

                  {r && (
                    <div
                      className={`rounded border p-2 text-xs space-y-1 ${
                        r.error
                          ? "border-rose-500/30 bg-rose-500/10 text-rose-100"
                          : "border-emerald-500/30 bg-emerald-500/10 text-emerald-100"
                      }`}
                      data-testid={`admin-migration-result-${m.id}`}
                    >
                      <div className="flex items-center gap-1 font-medium">
                        {r.error ? (
                          <><AlertTriangle className="w-3.5 h-3.5" /> Failed</>
                        ) : (
                          <><CheckCircle2 className="w-3.5 h-3.5" /> Success</>
                        )}
                        <span className="text-white/40 ml-auto text-[10px]">
                          {new Date(r.ts).toLocaleTimeString()}
                        </span>
                      </div>
                      {r.error ? (
                        <div className="font-mono whitespace-pre-wrap break-words">{r.error}</div>
                      ) : (
                        <ul className="space-y-0.5">
                          {(r.results || []).map((step, i) => (
                            <li key={i} className="font-mono break-words">
                              <span className="text-white/50">{step.step}:</span> {step.status}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
